// CardImageCache: A service for caching and managing card images
// This reduces network requests and improves performance when displaying cards
import { deckStorageService } from './deck-storage-service';
import { USE_LOCAL_ASSETS } from '@/config/constants';

class CardImageCache {
  private cache: Map<string, HTMLImageElement> = new Map();
  private preloadQueue: string[] = [];
  private isPreloading: boolean = false;
  private maxConcurrentLoads: number = 5; // Limit concurrent image loads
  
  // Get image from cache or create a new one
  getImage(url: string): HTMLImageElement | null {
    // Return from cache if exists
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }
    
    return null;
  }
  
  // Load an image and add to cache
  async loadImage(url: string): Promise<HTMLImageElement> {
    // Return from cache if exists
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }
    
    // Try to get local URL first if we're on a native platform
    let imageUrl = url;
    if (deckStorageService.isNativePlatform()) {
      const localUrl = await deckStorageService.getLocalImageUrl(url);
      if (localUrl) {
        imageUrl = localUrl;
      }
    }
    
    // Create and load new image
    const img = new Image();
    
    try {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
        img.src = imageUrl;
      });
      
      // Add to cache
      this.cache.set(url, img);
      return img;
    } catch (error) {
      console.warn(`Error loading image: ${imageUrl}`, error);
      
      // If local image failed, try remote as fallback
      if (imageUrl !== url) {
        // console.log('Local image failed, falling back to remote URL:', url);
        try {
          const remoteImg = new Image();
          await new Promise<void>((resolve, reject) => {
            remoteImg.onload = () => resolve();
            remoteImg.onerror = () => reject(new Error(`Failed to load remote image: ${url}`));
            remoteImg.src = url;
          });
          
          // Add to cache
          this.cache.set(url, remoteImg);
          return remoteImg;
        } catch (remoteError) {
          console.warn(`Failed to load remote image fallback: ${url}`, remoteError);
          throw remoteError;
        }
      }
      
      throw error;
    }
  }
  
  // Preload a collection of images in the background
  preloadDeckImages(deckUrl: string, cardCodes: string[]): void {
    // Feature flag: Disable preloading when using local assets
    if (USE_LOCAL_ASSETS) {
      console.log('Preloading disabled in local asset mode.');
      return;
    }
    
    const imageUrls = cardCodes.map(code => `${deckUrl}/${code}.svg`);
    
    // Add back card for completeness
    imageUrls.push(`${deckUrl}/back.svg`);
    
    // Add to preload queue without duplicates
    this.preloadQueue = [...new Set([...this.preloadQueue, ...imageUrls])];
    
    // Start preloading if not already in progress
    if (!this.isPreloading) {
      this.startPreloading();
    }
  }
  
  // Preload entire deck - useful when a player selects a specific deck
  preloadFullDeck(deckUrl: string): void {
    // Feature flag: Disable preloading when using local assets
    if (USE_LOCAL_ASSETS) {
      console.log('Preloading disabled in local asset mode.');
      return;
    }
    
    // Generate all possible card codes for a typical deck
    const suits = ['A', 'B', 'C', 'D'];
    const cardCodes: string[] = [];
    
    // Generate values 6-13 (typically 6, 7, 8, 9, 10, 11(J), 12(Q), 13(K))
    for (const suit of suits) {
      for (let value = 6; value <= 13; value++) {
        cardCodes.push(`${suit}${value}`);
      }
    }
    
    this.preloadDeckImages(deckUrl, cardCodes);
  }
  
  // Start preloading images in batches
  private async startPreloading(): Promise<void> {
    this.isPreloading = true;
    
    while (this.preloadQueue.length > 0) {
      // Take a batch of URLs to load concurrently
      const batch = this.preloadQueue.splice(0, this.maxConcurrentLoads);
      
      // Load batch concurrently
      await Promise.allSettled(
        batch.map(async (url) => {
          // Skip if already cached
          if (this.cache.has(url)) return;
          
          try {
            await this.loadImage(url);
          } catch (error) {
            console.warn(`Failed to preload image: ${url}`, error);
          }
        })
      );
    }
    
    this.isPreloading = false;
  }
  
  // Get DOM-ready image URL (from cache if available)
  getImageUrl(url: string): string {
    // If we have a cached image, create an object URL
    // This is a more advanced technique that could be implemented
    // for true caching, but for now we'll just return the original URL
    return url;
  }
  
  // Clear cache for specific URL pattern or all if no pattern provided
  clearCache(urlPattern?: string): void {
    if (urlPattern) {
      // Remove all URLs matching the pattern
      for (const url of this.cache.keys()) {
        if (url.includes(urlPattern)) {
          this.cache.delete(url);
        }
      }
    } else {
      this.cache.clear();
    }
  }
}

// Create singleton instance
export const cardImageCache = new CardImageCache();